/**
 * A mandatory field the operator has not filled, shown where it is (user, 2026-09-30): the action
 * button stays clickable, and a click with gaps takes the page to the first one, paints every one
 * of them red and names them in the snackbar. Design: docs/designs/missing-field-validation.md.
 *
 * A page wraps its form in `MissingFieldsProvider`. A control opts in with `useFieldState(key)` —
 * the key is its namespaced name (`header:arrival_date`, `transport:vehicle_number`,
 * `line:<lineId>:dispatch_qty`) and its DOM id is where the jump lands. A button runs
 * `guard(blockers, action)`: blockers left → `refuse`, none → the action.
 *
 * The snackbar sits in the middle of the screen and takes the focus (user, 2026-09-30), so the
 * operator cannot miss it: OK, Enter or Esc closes it and puts the cursor in the first gap, which the
 * page has already scrolled to and painted red behind it.
 *
 * The red lives here, in React, not on the DOM: an autosave re-render or a remounted lot card must
 * not wipe it. It clears by value — the moment the field is no longer empty, whoever filled it (the
 * operator, a Party pick, a feed prefill) — and a field emptied again stays neutral until the next
 * click.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CircleAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api-client";
import { usePrefs } from "@/lib/prefs";
import { beep } from "@/lib/sound";

export interface Blocker {
  /** What the snackbar says: a line-scoped one names its lot, "Lot 2 · Dispatch Qty". */
  label: string;
  /** The field it points at. None → not a field (bags left, SLOC balance): snackbar only. */
  key?: string | undefined;
  /** `missing` (default) is listed under "Fill in:"; `invalid` shows its own message. */
  kind?: "missing" | "invalid" | undefined;
  /** For `invalid`: the reason, usually the server's own sentence. */
  message?: string | undefined;
  /** A locked field is named but never painted or focused: the operator cannot change it here. */
  editable?: boolean | undefined;
  /**
   * Where the jump lands when no control registered the key — for a form that paints its own
   * errors (`invalid` from its own state) and only wants the notice and the jump.
   */
  elementId?: string | undefined;
}

type Mark = { label: string; kind: "missing" | "invalid" };

/** What a field wrapper (`GField`, `Field`) needs to take part: its key and its current value. */
export interface MissingSpec {
  key: string;
  value: unknown;
  isEmpty?: ((v: unknown) => boolean) | undefined;
}
type Reveal = {
  match: (key: string) => boolean;
  open: (key: string) => void | Promise<void>;
};

interface Api {
  missing: ReadonlyMap<string, Mark>;
  refuse: (blockers: readonly Blocker[]) => void;
  clear: (key: string) => void;
  register: (key: string, elementId: string) => () => void;
  registerReveal: (r: Reveal) => () => void;
  focusFirst: (keys: readonly string[]) => boolean;
}

const Ctx = createContext<Api | null>(null);

/** The providers on screen, innermost last: a server refusal lands in the one the operator is in. */
const mounted: Api[] = [];

const MAX_NAMES = 4;

/** Names, at most four, then "+N more". Exported for tests. */
export function fillInText(labels: readonly string[]): string {
  const unique = [...new Set(labels)];
  const shown = unique.slice(0, MAX_NAMES).join(", ");
  const more = unique.length - MAX_NAMES;
  return `Fill in: ${shown}${more > 0 ? ` +${more} more` : ""}`;
}

/** The snackbar for a refusal: missing names first, then any reasons that are not a blank field. */
export function refusalToast(blockers: readonly Blocker[]): {
  title: string;
  description?: string;
} {
  const names = blockers
    .filter(
      (b) =>
        (b.kind ?? "missing") === "missing" && b.editable !== false && b.key
    )
    .map((b) => b.label);
  const others = [
    ...new Set(
      blockers
        .filter(
          (b) =>
            !(
              (b.kind ?? "missing") === "missing" &&
              b.editable !== false &&
              b.key
            )
        )
        .map((b) =>
          b.kind === "invalid"
            ? (b.message ?? b.label)
            : b.editable === false
              ? `${b.label} is locked — fix it at the source`
              : b.label
        )
    ),
  ];
  if (names.length)
    return {
      title: fillInText(names),
      ...(others.length ? { description: others.join(" · ") } : {}),
    };
  return {
    title: others[0] ?? "Something is still missing",
    ...(others.length > 1 ? { description: others.slice(1).join(" · ") } : {}),
  };
}

const TOAST_ID = "missing-fields";

/**
 * A forgotten mandatory field gets the refusal sound too (unless turned off in Profile); server
 * "invalid" answers stay silent.
 */
function beepIfMissing(blockers: readonly Blocker[]) {
  if (!usePrefs.getState().formSound) return;
  if (blockers.some((b) => (b.kind ?? "missing") === "missing")) beep(false);
}

/** A page with no provider (not converted yet): the plain toast, at least centred. */
function showToast(blockers: readonly Blocker[]) {
  beepIfMissing(blockers);
  const t = refusalToast(blockers);
  toast.error(t.title, {
    id: TOAST_ID,
    position: "top-center",
    ...(t.description ? { description: t.description } : {}),
  });
}

type Notice = { title: string; description?: string | undefined };

/**
 * The refusal, centred and focused. Not a modal: the red fields behind it stay visible, and a click
 * anywhere outside closes it the same as OK.
 */
function MissingNotice({
  notice,
  onClose,
}: {
  notice: Notice;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    box.current
      ?.querySelector<HTMLButtonElement>("button")
      ?.focus({ preventScroll: true });
  }, [notice]);
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/10 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={box}
        role="alertdialog"
        aria-labelledby="missing-notice-title"
        aria-describedby={
          notice.description ? "missing-notice-desc" : undefined
        }
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="flex w-full max-w-md items-start gap-3 rounded-xl border border-danger-border bg-surface-raised p-4 shadow-pop"
      >
        <CircleAlert
          aria-hidden
          className="mt-0.5 h-5 w-5 shrink-0 text-danger"
        />
        <div className="min-w-0 flex-1">
          <p
            id="missing-notice-title"
            className="text-sm font-semibold text-danger-text"
          >
            {notice.title}
          </p>
          {notice.description && (
            <p
              id="missing-notice-desc"
              className="mt-1 text-sm text-ink-secondary"
            >
              {notice.description}
            </p>
          )}
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={onClose}>
              OK
            </Button>
          </div>
        </div>
      </div>
    </div>,
    // Inside an open dialog or sheet, the notice has to be in it: the dialog traps the focus and
    // treats a click outside it as "close".
    [
      ...document.querySelectorAll<HTMLElement>(
        '[role="dialog"][data-state="open"]'
      ),
    ].pop() ?? document.body
  );
}

const nextFrame = () =>
  new Promise<void>((r) => requestAnimationFrame(() => r()));

/** The control to put the cursor in: the element itself, or the first focusable inside it. */
function focusable(el: HTMLElement): HTMLElement {
  if (el.matches("input, select, textarea, button, [tabindex]")) return el;
  return (
    el.querySelector<HTMLElement>(
      "input:not([type=hidden]), select, textarea, button, [tabindex]"
    ) ?? el
  );
}

export function MissingFieldsProvider({ children }: { children: ReactNode }) {
  const [missing, setMissing] = useState<ReadonlyMap<string, Mark>>(
    () => new Map()
  );
  const ids = useRef(new Map<string, string>());
  const reveals = useRef<Reveal[]>([]);
  const [notice, setNotice] = useState<Notice | null>(null);
  /** Where the cursor goes when the notice closes: the first gap on the page. */
  const focusAfter = useRef<HTMLElement | null>(null);
  const closeNotice = useCallback(() => {
    setNotice(null);
    const el = focusAfter.current;
    focusAfter.current = null;
    if (el) requestAnimationFrame(() => el.focus({ preventScroll: true }));
  }, []);

  const clear = useCallback((key: string) => {
    setMissing((m) => {
      if (!m.has(key)) return m;
      const next = new Map(m);
      next.delete(key);
      return next;
    });
  }, []);

  const register = useCallback((key: string, elementId: string) => {
    ids.current.set(key, elementId);
    return () => {
      if (ids.current.get(key) === elementId) ids.current.delete(key);
    };
  }, []);

  const registerReveal = useCallback((r: Reveal) => {
    reveals.current.push(r);
    return () => {
      reveals.current = reveals.current.filter((x) => x !== r);
    };
  }, []);

  const refuse = useCallback((blockers: readonly Blocker[]) => {
    beepIfMissing(blockers);
    toast.dismiss(TOAST_ID);
    focusAfter.current = null;
    setNotice(refusalToast(blockers));
    const marked = blockers.filter(
      (b): b is Blocker & { key: string } => !!b.key && b.editable !== false
    );
    setMissing(
      new Map(
        marked.map((b) => [
          b.key,
          { label: b.label, kind: b.kind ?? "missing" },
        ])
      )
    );
    const first = marked[0];
    if (!first) return;
    void (async () => {
      // A field in a closed lot card or another step is not on the page yet: open it first, then
      // wait for React to put it there before looking for it.
      const reveal = reveals.current.find((r) => r.match(first.key));
      if (reveal) await reveal.open(first.key);
      await nextFrame();
      await nextFrame();
      const els = marked
        .map((b) => ids.current.get(b.key) ?? b.elementId)
        .map((id) => (id ? document.getElementById(id) : null))
        .filter((el): el is HTMLElement => el !== null);
      // "First" is the first on the page, which is not always the first in the list.
      els.sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      );
      const target = els[0];
      if (!target) return;
      target.scrollIntoView?.({ block: "center", behavior: "smooth" });
      // The notice has the focus now; the field gets it when the notice closes.
      focusAfter.current = focusable(target);
    })();
  }, []);

  /**
   * The cursor to the first of these fields on the page, with no notice and no red: a way in to
   * what is left, not a refusal (Update SLOC, user 2026-10-21). A key with no control on screen,
   * or a disabled one, is passed over; false when none of them was there.
   */
  const focusFirst = useCallback((keys: readonly string[]) => {
    const els = keys
      .map((k) => ids.current.get(k))
      .map((id) => (id ? document.getElementById(id) : null))
      .filter((el): el is HTMLElement => el !== null)
      .map(focusable)
      .filter((el) => !el.matches(":disabled"));
    els.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );
    const target = els[0];
    if (!target) return false;
    target.scrollIntoView?.({ block: "center", behavior: "smooth" });
    target.focus({ preventScroll: true });
    return true;
  }, []);

  const api = useMemo<Api>(
    () => ({ missing, refuse, clear, register, registerReveal, focusFirst }),
    [missing, refuse, clear, register, registerReveal, focusFirst]
  );

  // The latest api for server refusals raised outside any component (a mutation's onError).
  const live = useRef(api);
  useEffect(() => {
    live.current = api;
  }, [api]);
  useEffect(() => {
    const proxy: Api = {
      get missing() {
        return live.current.missing;
      },
      refuse: (b) => live.current.refuse(b),
      clear: (k) => live.current.clear(k),
      register: (k, id) => live.current.register(k, id),
      registerReveal: (r) => live.current.registerReveal(r),
      focusFirst: (k) => live.current.focusFirst(k),
    };
    mounted.push(proxy);
    return () => {
      const i = mounted.lastIndexOf(proxy);
      if (i >= 0) mounted.splice(i, 1);
    };
  }, []);

  return (
    <Ctx.Provider value={api}>
      {children}
      {notice && <MissingNotice notice={notice} onClose={closeNotice} />}
    </Ctx.Provider>
  );
}

/**
 * `refuse` and `guard` for a button. Outside a provider it still names the gaps in the snackbar —
 * a page not converted yet loses the jump and the red, never the message.
 */
export function useMissingFields() {
  const api = useContext(Ctx);
  const refuse = useCallback(
    (blockers: readonly Blocker[]) =>
      api ? api.refuse(blockers) : showToast(blockers),
    [api]
  );
  const guard = useCallback(
    (blockers: readonly Blocker[], action: () => void) => {
      if (blockers.length) refuse(blockers);
      else action();
    },
    [refuse]
  );
  const focusFirst = useCallback(
    (keys: readonly string[]) => api?.focusFirst(keys) ?? false,
    [api]
  );
  return {
    refuse,
    guard,
    focusFirst,
    registerReveal: api?.registerReveal,
  };
}

/** Open whatever hides a field (a lot card, a step, a tab) before the page jumps to it. */
export function useReveal(
  match: (key: string) => boolean,
  open: (key: string) => void | Promise<void>
) {
  const api = useContext(Ctx);
  const latest = useRef({ match, open });
  useEffect(() => {
    latest.current = { match, open };
  });
  useEffect(
    () =>
      api?.registerReveal({
        match: (k) => latest.current.match(k),
        open: (k) => latest.current.open(k),
      }),
    [api]
  );
}

/** "Not marked yet", distinct from every value a field can hold. */
const NONE = Symbol("none");

const blank = (v: unknown) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

/**
 * A control's part: is it one the operator was sent to? `elementId` is the DOM id the jump lands
 * on. `isEmpty` says what counts as still missing — a quantity that must be above zero passes one
 * that counts 0 too.
 */
export function useFieldState(
  key: string | undefined,
  {
    value,
    elementId,
    isEmpty = blank,
  }: {
    value: unknown;
    elementId?: string | undefined;
    isEmpty?: ((v: unknown) => boolean) | undefined;
  }
): { invalid: boolean } {
  const api = useContext(Ctx);
  const mark = key && api ? api.missing.get(key) : undefined;

  useEffect(() => {
    if (!key || !elementId || !api) return;
    return api.register(key, elementId);
    // `api` changes with every mark; registration only cares about the key and the element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, elementId, api?.register]);

  // The value the field had when it was marked: a change away from it is what clears the red.
  const markedWith = useRef<unknown>(NONE);
  useEffect(() => {
    if (!mark) {
      markedWith.current = NONE;
      return;
    }
    if (markedWith.current === NONE) {
      markedWith.current = value;
      return;
    }
    if (Object.is(value, markedWith.current)) return;
    // A blank field clears once it holds something; a refused value clears once it is changed.
    if (mark.kind === "invalid" || !isEmpty(value)) api?.clear(key!);
  }, [mark, value, key, api, isEmpty]);

  return { invalid: !!mark };
}

/** Maps a server refusal's field names to this form's keys. */
export interface FieldAdapter {
  /** A 422's `details.field`, or a 400's JSON pointer (`/transport/vehicleNumber`). */
  keyOf: (field: string) => string | undefined;
  labelOf: (key: string) => string;
}

/** A server refusal as blockers, or `null` when it names no field this form knows. */
export function blockersFromApiError(
  e: unknown,
  adapter: FieldAdapter
): Blocker[] | null {
  if (!(e instanceof ApiError)) return null;
  const d = e.details;
  if (Array.isArray(d)) {
    const out = d.flatMap((x: { path?: unknown; message?: unknown }) => {
      const key =
        typeof x.path === "string" ? adapter.keyOf(x.path) : undefined;
      if (!key) return [];
      const label = adapter.labelOf(key);
      const msg = typeof x.message === "string" ? x.message : "is not valid";
      return [
        { key, label, kind: "invalid" as const, message: `${label}: ${msg}` },
      ];
    });
    return out.length ? out : null;
  }
  const field =
    d &&
    typeof d === "object" &&
    typeof (d as { field?: unknown }).field === "string"
      ? (d as { field: string }).field
      : undefined;
  const key = field ? adapter.keyOf(field) : undefined;
  if (!key) return null;
  return [
    { key, label: adapter.labelOf(key), kind: "invalid", message: e.message },
  ];
}

/**
 * A server refusal that names a field goes through the same jump, red and snackbar as a click on
 * a form with gaps. Returns false when it named nothing this form knows — the caller toasts as before.
 */
export function refuseApiError(e: unknown, adapter: FieldAdapter): boolean {
  const blockers = blockersFromApiError(e, adapter);
  const api = mounted[mounted.length - 1];
  if (!blockers || !api) return false;
  api.refuse(blockers);
  return true;
}

/**
 * For a control that is not one box — a radio group, a checklist: the area around it goes red and
 * the jump lands on its first control.
 */
export function MissingArea({
  missingKey,
  value,
  id,
  className,
  children,
}: {
  missingKey: string;
  value: unknown;
  id: string;
  className?: string | undefined;
  children: ReactNode;
}) {
  const { invalid } = useFieldState(missingKey, { value, elementId: id });
  return (
    <div
      id={id}
      data-missing-area=""
      aria-invalid={invalid || undefined}
      className={className}
    >
      {children}
    </div>
  );
}

/** Messages that mean "nothing there yet", as opposed to "what is there is wrong". */
const BLANK_MESSAGE =
  /^(required|pick|choose|select|enter the|fill|please (enter|select|pick))/i;

/**
 * A form that keeps its own `errors` (key → message) and its own red: the same errors as blockers,
 * named by the label on screen, so `refuse` can show the notice and jump. `controls` maps an error
 * key to the id of its control, in screen order.
 */
export function blockersFromErrors(
  errors: Readonly<Record<string, string | undefined>>,
  controls: ReadonlyArray<readonly [string, string]>
): Blocker[] {
  const labelOf = (id: string) => {
    const el = document.getElementById(id);
    const label =
      document.querySelector(`label[for="${CSS.escape(id)}"]`)?.textContent ??
      el?.getAttribute("aria-label") ??
      (el?.getAttribute("aria-labelledby")
        ? document.getElementById(el.getAttribute("aria-labelledby")!)
            ?.textContent
        : null);
    return label?.replace(/\s*\*\s*$/, "").trim() || undefined;
  };
  const known = new Map(controls);
  const order = [
    ...controls.map(([k]) => k).filter((k) => errors[k]),
    ...Object.keys(errors).filter((k) => errors[k] && !known.has(k)),
  ];
  return order.map((k) => {
    const id = known.get(k);
    const label = (id && labelOf(id)) || k;
    const msg = errors[k]!;
    return BLANK_MESSAGE.test(msg)
      ? { key: k, label, elementId: id }
      : {
          key: k,
          label,
          elementId: id,
          kind: "invalid" as const,
          message: `${label}: ${msg}`,
        };
  });
}

/**
 * `guard` for a button that sits inside the provider it needs but in a component that does not — a
 * dialog's footer, where the provider wraps the dialog's own content.
 */
export function MissingGuard({
  blockers,
  children,
}: {
  blockers: readonly Blocker[];
  children: (run: (action: () => void) => void) => ReactNode;
}) {
  const { guard } = useMissingFields();
  return <>{children((action) => guard(blockers, action))}</>;
}

/**
 * `refuse` from outside any component: a form's submit handler, a react-hook-form `onInvalid`. It
 * lands in the provider the operator is in — the app root has one, so there always is one.
 */
export function refuseNow(blockers: readonly Blocker[]): void {
  const api = mounted[mounted.length - 1];
  if (api) api.refuse(blockers);
  else showToast(blockers);
}

interface FormErrorLike {
  message?: unknown;
  type?: unknown;
  ref?: { id?: string; name?: string } | undefined;
}

/**
 * react-hook-form's errors as blockers, in the order the form declared its fields. RHF paints its
 * own fields red and keeps them so; this only names them in the notice and aims the jump at the
 * control RHF registered (`ref`).
 */
export function blockersFromFormErrors(errors: object): Blocker[] {
  const out: Blocker[] = [];
  const walk = (node: unknown, path: string) => {
    if (!node || typeof node !== "object") return;
    const e = node as FormErrorLike;
    if (typeof e.message === "string" || e.ref) {
      const el =
        (e.ref?.id && document.getElementById(e.ref.id)) ||
        (e.ref?.name &&
          document.querySelector<HTMLElement>(
            `[name="${CSS.escape(e.ref.name)}"]`
          )) ||
        null;
      if (el && !el.id) el.id = `rhf-${path.replace(/[^\w-]/g, "-")}`;
      const id = el?.id;
      const label =
        (id &&
          (document
            .querySelector(`label[for="${CSS.escape(id)}"]`)
            ?.textContent?.replace(/\s*\*\s*$/, "")
            .trim() ||
            el?.getAttribute("aria-label"))) ||
        path;
      const msg = typeof e.message === "string" ? e.message : "";
      out.push(
        e.type === "required" || !msg || BLANK_MESSAGE.test(msg)
          ? { key: `form:${path}`, label, elementId: id }
          : {
              key: `form:${path}`,
              label,
              elementId: id,
              kind: "invalid",
              message: `${label}: ${msg}`,
            }
      );
      return;
    }
    for (const [k, v] of Object.entries(node))
      if (k !== "root") walk(v, path ? `${path}.${k}` : k);
  };
  walk(errors, "");
  return out;
}
